import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Spinner, Alert, Modal } from "react-bootstrap";
import { IoArrowBackOutline, IoArrowForwardOutline } from "react-icons/io5";
import { FaEye, FaMicrophone, FaQuestionCircle, FaStop } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import WebService from "../../Services/WebService";

import { useCountdown } from "./hooks/useCountdown";
import {
  useAttemptEngine,
  AttemptRow,
  percent,
} from "./hooks/useAttemptEngine";
import LessonResultModal from "./components/LessonResultModal";

import type {
  Response,
  ResponseSelect,
  ResponseNumeric,
  ResponseText,
  ResponseVoice,
  ResponseCanvas,
  Slide,
} from "../ManageQuestions/types";

import SelectWidget from "./components/widgets/SelectWidget";
import NumericWidget from "./components/widgets/NumericWidget";
import TextWidget from "./components/widgets/TextWidget";
import VoiceWidget from "./components/widgets/VoiceWidget";
import FeedbackModal from "./components/widgets/FeedbackModal";

// Unified SVG (image+planes), same sizing behavior as AddEditQuestion
import SVGRender from "./components/widgets/SVGRender";

import "./styles/attempt-player.scss";
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
function notifyReactNative(eventName: string, data: any = {}) {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        event: eventName,
        payload: data,
      }),
    );
  }
}
/* ---------------- helpers ---------------- */
const isWebView = () => {
  return /wv|Android/i.test(navigator.userAgent);
};
const isApp = !!window.ReactNativeWebView;
const isAppEnv = () => {
  return !!(window as any).ReactNativeWebView || isWebView();
};
const pickFemaleVoice = () => {
  if (!window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();

  if (!voices.length) return null;

  // English voices only
  const english = voices.filter((v) => v.lang?.toLowerCase().startsWith("en"));

  // Try to find female-type voices by name
  const female = english.find((v) =>
    /female|zira|samantha|google us english|google uk english|karen/i.test(
      v.name,
    ),
  );

  return female || english[0] || voices[0];
};

// Prefer a MALE English voice for the ATC controller
const pickMaleVoice = () => {
  if (!window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;

  const english = voices.filter((v) => v.lang?.toLowerCase().startsWith("en"));

  // Known male voices across OS/browsers
  const male = english.find((v) =>
    /david|mark|daniel|google uk english male|microsoft david|microsoft mark|alex|fred|guy|male/i.test(
      v.name,
    ),
  );

  // Fallback: any English voice that is clearly NOT a female one
  const notFemale = english.find(
    (v) => !/female|zira|susan|samantha|karen|hazel|linda|catherine/i.test(v.name),
  );

  return male || notFemale || english[0] || voices[0];
};

// Prefer a DUTCH (nl-NL) voice for the ATC controller
const pickDutchVoice = () => {
  if (!window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;

  const dutch = voices.filter((v) => v.lang?.toLowerCase().startsWith("nl"));
  if (!dutch.length) return null;

  // Prefer a male Dutch voice if available (Xander / Frank / etc.)
  const male = dutch.find((v) =>
    /xander|frank|daniel|male|google nederlands/i.test(v.name),
  );

  return male || dutch[0];
};

// const speakSequence = (lines: string[]) => {
//   try {
//     const synth = window.speechSynthesis;
//     synth.cancel();

//     const items = lines.map((t) => (t || "").trim()).filter(Boolean);
//     if (!items.length) return;

//     const ctx = new (
//       window.AudioContext || (window as any).webkitAudioContext
//     )();

//     /* ===== RADIO STATIC (rough) ===== */
//     const startNoise = () => {
//       const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.25, ctx.sampleRate);
//       const data = buffer.getChannelData(0);

//       for (let i = 0; i < data.length; i++) {
//         data[i] = (Math.random() * 2 - 1) * 0.18; // 🔥 rough hiss
//       }

//       const src = ctx.createBufferSource();
//       src.buffer = buffer;
//       src.loop = true;

//       const band = ctx.createBiquadFilter();
//       band.type = "bandpass";
//       band.frequency.value = 800; // lower = more compressed
//       band.Q.value = 6.0; // very narrow radio band

//       const distort = ctx.createWaveShaper();
//       distort.curve = new Float32Array([-1, -1, -0.8, -0.4, 0, 0.4, 0.8, 1, 1]);
//       distort.oversample = "4x";

//       const gain = ctx.createGain();
//       gain.gain.value = 0.65;

//       src.connect(band).connect(distort).connect(gain).connect(ctx.destination);

//       src.start();
//       return () => {
//         src.stop();
//         src.disconnect();
//       };
//     };

//     /* ===== WALKIE-TALKIE CHIRP ===== */
//     const chirp = (ms = 90) => {
//       const o = ctx.createOscillator();
//       const g = ctx.createGain();
//       o.type = "square";
//       o.frequency.value = 1500;
//       g.gain.value = 0.25;
//       o.connect(g).connect(ctx.destination);
//       o.start();
//       setTimeout(() => o.stop(), ms);
//     };

//     const voice = pickFemaleVoice();

//     chirp(80);
//     let stopNoise: (() => void) | null = null;
//     let i = 0;

//     const speakNext = () => {
//       if (i >= items.length) {
//         stopNoise?.();
//         chirp(100);
//         return;
//       }

//       stopNoise = startNoise();

//       const u = new SpeechSynthesisUtterance(items[i]);
//       u.voice = voice;
//       u.rate = 2.0; // MAX browser stable speed
//       u.pitch = 0.65; // Lower to avoid chipmunk
//       u.volume = 0.9;

//       u.onend = () => {
//         stopNoise?.();
//         i++;
//         setTimeout(speakNext, 120);
//       };

//       synth.speak(u);
//     };

//     speakNext();
//   } catch {
//     /* ignore */
//   }
// };

// const hasAtc = (s?: Slide | null) => {
//   if (!s) return false;

//   // ✅ NEW: API-based ATC
//   const apiAtc =
//     (s as any).atc?.text && String((s as any).atc.text).trim().length > 0;

//   // legacy fallback (optional)
//   const legacy =
//     (s as any).atcText && String((s as any).atcText).trim().length > 0;

//   // script fallback (optional)
//   const script = (s as any).script as Array<{ text?: string }> | undefined;
//   const scriptHas =
//     Array.isArray(script) && script.some((l) => (l.text || "").trim());

//   return apiAtc || legacy || scriptHas;
// };
// const speakSequence = (lines: string[]) => {
//   if (isAppEnv()) return; // 🚫 disable inside app

//   if (!window.speechSynthesis) return;

//   const synth = window.speechSynthesis;
//   const items = lines.map(t => t?.trim()).filter(Boolean);
//   if (!items.length) return;

//   synth.cancel();

//   let i = 0;

//   const speakNext = () => {
//     if (i >= items.length) return;

//     const u = new SpeechSynthesisUtterance(items[i]);
//     u.rate = 1.6;
//     u.pitch = 0.9;

//     u.onend = () => {
//       i++;
//       setTimeout(speakNext, 120);
//     };

//     synth.speak(u);
//   };

//   speakNext();
// };
const runBrowserRadio = (lines: string[]) => {
  const items = lines.map((t) => t?.trim()).filter(Boolean);
  if (!items.length) return;

  const synth = window.speechSynthesis;
  if (!synth) return;

  synth.cancel();

  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;

  const ctx = AudioCtx ? new AudioCtx() : null;

  let stopHiss: (() => void) | null = null;

  // Subtle band-passed static that sits UNDER the voice (radio background hiss).
  const startRadioHiss = () => {
    if (!ctx) return null;

    const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i++) {
      const base = (Math.random() * 2 - 1) * 0.85;
      // frequent crackle so it sounds like a busy, garbled radio channel
      const crackle = Math.random() > 0.99 ? Math.random() * 2 - 1 : 0;
      data[i] = base + crackle;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    // Narrow band-pass right in the speech band so it masks/garbles the voice
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 1400;
    band.Q.value = 6;

    const gain = ctx.createGain();
    gain.gain.value = 0.38; // heavier: partly masks the voice (harder to understand)

    noise.connect(band).connect(gain).connect(ctx.destination);
    noise.start();

    return () => {
      try {
        noise.stop();
      } catch {}
    };
  };

  // Short squelch "click" on PTT press / release (quick fade so it clicks, not beeps)
  const squelch = (freq = 1200, ms = 55) => {
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const g = ctx.createGain();

    osc.type = "square";
    osc.frequency.value = freq;

    const now = ctx.currentTime;
    g.gain.setValueAtTime(0.16, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + ms / 1000);

    osc.connect(g).connect(ctx.destination);
    osc.start();

    setTimeout(() => {
      try {
        osc.stop();
      } catch {}
    }, ms + 10);
  };

  const maleVoice = pickMaleVoice();

  let i = 0;

  const speakNext = () => {
    if (i >= items.length) {
      stopHiss?.();
      squelch(900, 70); // end-of-transmission click
      return;
    }

    squelch(1300, 55); // PTT press click
    stopHiss = startRadioHiss();

    const u = new SpeechSynthesisUtterance(items[i]);

    // English male controller voice (browser fallback)
    if (maleVoice) {
      u.voice = maleVoice;
    }
    u.lang = "en-US";
    u.rate = 1.6; // clipped ATC cadence
    u.pitch = 0.9; // natural male
    u.volume = 0.8;

    u.onend = () => {
      stopHiss?.();
      i++;
      setTimeout(speakNext, 140);
    };

    synth.speak(u);
  };

  speakNext();
};
// Translate a phrase to Dutch (free API; falls back to the original on failure)
const translateToDutch = async (text: string): Promise<string> => {
  const src = (text || "").trim();
  if (!src) return src;
  try {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(
        src,
      )}&langpair=en|nl`,
    );
    const data = await res.json();
    const t = data?.responseData?.translatedText;
    return typeof t === "string" && t.trim() ? t : src;
  } catch {
    return src;
  }
};

// Speak numbers digit-by-digit in Dutch (aviation style) + decimals -> "komma"
const NL_DIGITS = [
  "nul",
  "een",
  "twee",
  "drie",
  "vier",
  "vijf",
  "zes",
  "zeven",
  "acht",
  "negen",
];
// ICAO phonetic alphabet (used only for hyphenated aircraft callsigns, e.g. PH-SPX)
const NATO_ALPHABET: Record<string, string> = {
  A: "Alpha",
  B: "Bravo",
  C: "Charlie",
  D: "Delta",
  E: "Echo",
  F: "Foxtrot",
  G: "Golf",
  H: "Hotel",
  I: "India",
  J: "Juliett",
  K: "Kilo",
  L: "Lima",
  M: "Mike",
  N: "November",
  O: "Oscar",
  P: "Papa",
  Q: "Quebec",
  R: "Romeo",
  S: "Sierra",
  T: "Tango",
  U: "Uniform",
  V: "Victor",
  W: "Whiskey",
  X: "X-ray",
  Y: "Yankee",
  Z: "Zulu",
};

// Spell an ident (letters -> phonetic, digits -> Dutch words), dropping hyphens
const spellIdent = (token: string): string =>
  token
    .split("")
    .map((ch) => {
      if (/[A-Z]/.test(ch)) return NATO_ALPHABET[ch] || ch;
      if (/[0-9]/.test(ch)) return NL_DIGITS[Number(ch)];
      return "";
    })
    .filter(Boolean)
    .join(" ");

const toSpokenDutch = (text: string): string => {
  if (!text) return text;
  return (
    text
      // ONLY hyphenated aircraft callsigns (e.g. "PH-SPX", "G-ABCD") -> phonetic.
      // Requires 1-2 letters + hyphen + 2-6 letters/digits, so ATIS/ILS/plain
      // words (no hyphen) and single letters are never touched.
      .replace(/\b[A-Z]{1,2}-[A-Z0-9]{2,6}\b/g, (m) => spellIdent(m))
      .replace(/(\d)[.,](\d)/g, "$1 komma $2") // 125.2 -> 125 komma 2
      .replace(/\d/g, (d) => ` ${NL_DIGITS[Number(d)]} `) // each digit -> word
      .replace(/\s{2,}/g, " ")
      .trim()
  );
};

/* =========================================================================
   ATC VOICE — matches arsim.ai exactly: Google Cloud TTS "en-US-Neural2-A",
   pitch -2, rate 1.43, aviation-English phonetic text, radio effect on top.
   Falls back to StreamElements (AWS Polly, no key) then browser speech.
   ========================================================================= */

// arsim's exact voice config (en-US-Neural2-A, pitch -2, rate 1.43) now lives
// server-side in the backend /tts proxy so the Google API key never reaches the
// browser. See TtsController@speak.

// Fallback voice (StreamElements / AWS Polly, no key) — English male
const RADIO_TTS_VOICE = "Matthew";

// Aviation-English phonetic: digits (Tree/Fife/Niner) + hyphenated callsigns
const EN_DIGITS = [
  "zero",
  "one",
  "two",
  "tree",
  "four",
  "fife",
  "six",
  "seven",
  "eight",
  "niner",
];
const spellIdentEn = (token: string): string =>
  token
    .split("")
    .map((ch) => {
      if (/[A-Z]/.test(ch)) return NATO_ALPHABET[ch] || ch;
      if (/[0-9]/.test(ch)) return EN_DIGITS[Number(ch)];
      return "";
    })
    .filter(Boolean)
    .join(" ");
const toAviationEnglish = (text: string): string => {
  if (!text) return text;
  return text
    .replace(/\b[A-Z]{1,2}-[A-Z0-9]{2,6}\b/g, (m) => spellIdentEn(m)) // callsign
    .replace(/(\d)[.,](\d)/g, "$1 decimal $2") // 125.2 -> 125 decimal 2
    .replace(/\d/g, (d) => ` ${EN_DIGITS[Number(d)]} `) // digits -> words
    .replace(/\s{2,}/g, " ")
    .trim();
};

// Google Cloud TTS via backend proxy -> MP3 ArrayBuffer (null on failure).
// Calls POST /tts (backend adds the API key + arsim voice params); we just
// decode the returned base64 MP3. Falls back to Polly/browser speech on failure.
const runGoogleTTS = async (text: string): Promise<ArrayBuffer | null> => {
  try {
    const data: any = await WebService.postAPI({
      action: "/tts",
      body: { text },
    });
    const b64: string | undefined = data?.audioContent;
    if (!b64) return null;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  } catch {
    return null;
  }
};

// One line -> MP3 ArrayBuffer: Google TTS (exact arsim) then Polly fallback
const synthAudio = async (line: string): Promise<ArrayBuffer | null> => {
  const g = await runGoogleTTS(line);
  if (g) return g;
  try {
    const url =
      "https://api.streamelements.com/kappa/v2/speech?voice=" +
      encodeURIComponent(RADIO_TTS_VOICE) +
      "&text=" +
      encodeURIComponent(line);
    const resp = await fetch(url);
    if (!resp.ok) return null;
    return await resp.arrayBuffer();
  } catch {
    return null;
  }
};

const runRadioMp3 = async (lines: string[]): Promise<boolean> => {
  const items = lines.map((t) => t?.trim()).filter(Boolean);
  if (!items.length) return true;

  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtx) return false;

  const ctx: AudioContext = new AudioCtx();
  try {
    if (ctx.state === "suspended") await ctx.resume();
  } catch {}

  // Low band-passed static bed under the voice
  const startStatic = () => {
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
    const d = buffer.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.7;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 1600;
    band.Q.value = 5;
    const g = ctx.createGain();
    g.gain.value = 0.16; // static/"khrkhr" level — raise for more crackle, lower for less
    noise.connect(band).connect(g).connect(ctx.destination);
    noise.start();
    return () => {
      try {
        noise.stop();
      } catch {}
    };
  };

  const squelch = (freq: number, ms: number) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "square";
    o.frequency.value = freq;
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0.14, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + ms / 1000);
    o.connect(g).connect(ctx.destination);
    o.start();
    setTimeout(() => {
      try {
        o.stop();
      } catch {}
    }, ms + 10);
  };

  // Play a decoded voice buffer through a tinny, band-limited radio chain
  const playBuffer = (buf: AudioBuffer) =>
    new Promise<void>((resolve) => {
      const src = ctx.createBufferSource();
      src.buffer = buf;

      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 500; // cut lows -> tinny radio

      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 3400; // band-limited radio, but keep consonants clear

      const shaper = ctx.createWaveShaper();
      shaper.curve = makeDistortionCurve(40); // light radio distortion
      shaper.oversample = "2x";

      const g = ctx.createGain();
      g.gain.value = 0.8; // voice level — lower = quieter voice vs the khrkhr

      src
        .connect(hp)
        .connect(lp)
        .connect(shaper)
        .connect(g)
        .connect(ctx.destination);
      src.onended = () => resolve();
      src.start();
    });

  try {
    for (const line of items) {
      const arr = await synthAudio(line);
      if (!arr) throw new Error("tts failed");
      const buf = await ctx.decodeAudioData(arr.slice(0));

      squelch(1300, 55); // PTT press
      const stopStatic = startStatic();
      await playBuffer(buf);
      // let the final word ring out BEFORE the static cut + release click
      await new Promise((r) => setTimeout(r, 230));
      stopStatic();
      squelch(900, 70); // PTT release
      await new Promise((r) => setTimeout(r, 120));
    }
    return true;
  } catch {
    return false; // caller falls back to browser speech
  }
};

const speakSequence = async (lines: string[]) => {
  const items = lines.map((t) => t?.trim()).filter(Boolean);
  if (!items.length) return;

  // Aviation-English phonetic (matches arsim: Tree/Fife/Niner + callsign spelled)
  const spoken = items.map(toAviationEnglish);

  const isApp = !!(window as any).ReactNativeWebView;

  // 📱 If running inside React Native WebView
  if (isApp) {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: "ATC_SPEAK",
        payload: spoken.join(" "),
      }),
    );
    return; // 🚀 stop here
  }

  // 🌐 Google TTS (exact arsim voice) -> Polly -> browser speech, + radio effect
  const ok = await runRadioMp3(spoken);
  if (!ok) runBrowserRadio(spoken);
};

function makeDistortionCurve(amount: number) {
  const k = typeof amount === "number" ? amount : 50;
  const n_samples = 44100;
  const curve = new Float32Array(n_samples);
  const deg = Math.PI / 180;

  for (let i = 0; i < n_samples; ++i) {
    const x = (i * 2) / n_samples - 1;
    curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
  }

  return curve;
}

const hasAtc = (s?: Slide | null) => {
  if (!s) return false;

  // ✅ STRICT: only enable ATC if API atc object exists
  return Boolean(
    (s as any).atc &&
    typeof (s as any).atc.text === "string" &&
    (s as any).atc.text.trim().length > 0,
  );
};

const getAtcText = (s?: Slide | null): string => {
  if (!s) return "";

  if ((s as any).atc?.text) return (s as any).atc.text;

  const script = (s as any).script as Array<{ text?: string }> | undefined;

  if (script?.length) {
    return script
      .map((l) => l.text)
      .filter(Boolean)
      .join(" ");
  }

  return s.title || "";
};

// --- helper to compute "expected" answer text for the current response
const getRevealText = (resp?: Response): string => {
  if (!resp) return "";

  switch (resp.type) {
    case "select": {
      const r = resp as ResponseSelect;
      const firstCorrect = (r.options || []).find((o) => o.is_correct);
      return firstCorrect?.label ?? "";
    }
    case "numeric": {
      const r = resp as ResponseNumeric;
      const rng = r.evaluation?.range || {};
      if (typeof (rng as any).exact === "number")
        return String((rng as any).exact);
      const { min, max } = rng as { min?: number; max?: number };
      if (typeof min === "number" && typeof max === "number")
        return `${min} – ${max}`;
      if (typeof min === "number") return `≥ ${min}`;
      if (typeof max === "number") return `≤ ${max}`;
      return "";
    }
    case "text": {
      const r = resp as ResponseText;
      const patterns = r.evaluation?.patterns || [];
      const p0 = patterns[0];
      if (!p0) return "";
      if (typeof p0 === "string") return p0;
      return (p0 as any).pattern ?? "";
    }
    case "voice": {
      const r = resp as ResponseVoice;
      const sets = r.evaluation?.expected_keywords || [];
      // each set: join alternatives with " / ", then join sets with " . "
      const parts = sets
        .map((s) =>
          (s.any_of || [])
            .map((x) => (x || "").trim())
            .filter(Boolean)
            .join(" / "),
        )
        .filter(Boolean);
      return parts.join(". ");
    }
    case "canvas_action": {
      const r = resp as ResponseCanvas;
      const ids = r.evaluation?.correct_entity_ids || [];
      return ids[0] ?? "";
    }
    default:
      return "";
  }
};

/* ===== small sound hook for button clicks ===== */
// const useClickTone = () => {
//   const ctxRef = useRef<AudioContext | null>(null);
//   const ensure = () =>
//     (ctxRef.current ??= new (
//       window.AudioContext || (window as any).webkitAudioContext
//     )());
//   const play = (freq = 660, ms = 120) => {
//     const ctx = ensure();
//     const o = ctx.createOscillator();
//     const g = ctx.createGain();
//     o.type = "sine";
//     o.frequency.value = freq;
//     g.gain.value = 0.12;
//     o.connect(g);
//     g.connect(ctx.destination);
//     o.start();
//     setTimeout(() => {
//       o.stop();
//       o.disconnect();
//       g.disconnect();
//     }, ms);
//   };
//   return { play };
// };
const useClickTone = () => {
  const ctxRef = useRef<AudioContext | null>(null);

  const play = (freq = 660, ms = 120) => {
    if (isAppEnv()) return; // 🚫 disable in app

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;

    if (!AudioCtx) return;

    try {
      if (!ctxRef.current) {
        ctxRef.current = new AudioCtx();
      }

      const ctx = ctxRef.current;

      const o = ctx.createOscillator();
      const g = ctx.createGain();

      o.type = "sine";
      o.frequency.value = freq;
      g.gain.value = 0.1;

      o.connect(g);
      g.connect(ctx.destination);

      o.start();
      setTimeout(() => o.stop(), ms);
    } catch {
      // ignore
    }
  };

  return { play };
};

/* Progress bar */
type ProgMode = "idle" | "sync" | "sweep";

const ProgressSingle: React.FC<{
  value: number; // 0..100
  animateTrigger: number; // bump to transition width
  mode?: ProgMode;
  sweepMs?: number; // sweep duration override (ms)
  onAfterAnimate?: () => void; // called after the animation window
  showFeedback?: boolean;
  onFeedback?: () => void;
}> = ({
  value,
  animateTrigger,
  mode = "idle",
  sweepMs = 1400,
  onAfterAnimate,
  showFeedback,
  onFeedback,
}) => {
  const clamped = Math.max(0, Math.min(100, value));

  useEffect(() => {
    const delay = mode === "sweep" ? Math.max(600, sweepMs) : 420;
    const t = setTimeout(() => onAfterAnimate?.(), delay);
    return () => clearTimeout(t);
  }, [animateTrigger, mode, sweepMs, onAfterAnimate]);

  return (
    <div className="tap-prog-wrap">
      <div className="tap-prog-row">
        {/* <div className="tap-cap">0</div> */}
        <div className="tap-prog-center">
          <div className="tap-prog-track">
            {/* <div
              className={`tap-prog-fill ${mode === "sweep" ? "is-sweep" : ""}`} */}
            <div
              className={`tap-prog-fill
    ${mode === "sync" ? "recording" : ""}
    ${mode === "sweep" ? "is-sweep" : ""}
    ${showFeedback && !onFeedback ? "analyzing" : ""}
    ${showFeedback && onFeedback ? "result" : ""}
`}
              style={{
                width: `${clamped}%`,
                ...(mode === "sweep"
                  ? ({
                      animationDuration: `${sweepMs}ms`,
                    } as React.CSSProperties)
                  : {}),
              }}
              data-anim-key={animateTrigger}
            />
            {mode === "sync" && <div className="tap-prog-sync" aria-hidden />}
          </div>
          {showFeedback && (
            <div
              className="tap-feedback-link"
              onClick={onFeedback}
              role="button"
              tabIndex={0}
            >
              Show Feedback
            </div>
          )}
        </div>
        {/* <div className="tap-cap">100</div> */}
      </div>
    </div>
  );
};

const ProgressSingleForTap: React.FC<{
  value: number; // 0..100
  animateTrigger: number;
  mode?: ProgMode;
  sweepMs?: number;
  onAfterAnimate?: () => void;
  showFeedback?: boolean;
  onFeedback?: () => void;
  isAnalyzing?: boolean;
  leftCap?: React.ReactNode;
  rightCap?: React.ReactNode;
}> = ({
  value,
  animateTrigger,
  mode = "sweep",
  sweepMs = 1400,
  onAfterAnimate,
  showFeedback,
  onFeedback,
  isAnalyzing: isAnalyzingProp = false,
  leftCap = "0",
  rightCap = "100",
}) => {
  const clamped = Math.max(0, Math.min(100, value));
  // Prefer the explicit prop from the parent (true while server eval is pending);
  // fall back to the legacy heuristic for callers that don't pass it.
  const isAnalyzing = isAnalyzingProp || (showFeedback && !onFeedback);
  useEffect(() => {
    const delay = mode === "sweep" ? Math.max(600, sweepMs) : 420;
    const t = setTimeout(() => onAfterAnimate?.(), delay);
    return () => clearTimeout(t);
  }, [animateTrigger, mode, sweepMs, onAfterAnimate]);

  return (
    <div className="tap-prog-wrap" style={{ width: "100%" }}>
      <div className="tap-prog-row">
        {/* LEFT CAP */}
        <div className="tap-cap tap-cap-left">{leftCap}</div>

        <div className="tap-prog-center">
          {/* has-result: show the grey track under the score line, so a 0 (or
              low) score still shows the full bar instead of nothing. */}
          <div
            className={`tap-prog-track ${
              showFeedback && !isAnalyzing ? "has-result" : ""
            }`}
          >
            {/* <div
              className={`tap-prog-fill ${mode === "sweep" ? "is-sweep" : ""}`}
              style={{
                width: `${value}%`,
                ...(mode === "sweep"
                  ? ({
                      animationDuration: `${sweepMs}ms`,
                    } as React.CSSProperties)
                  : {}),
              }}
              data-anim-key={animateTrigger}
            /> */}
            <div
              className={`tap-prog-fill
    ${mode === "sync" ? "recording" : ""}
    ${mode === "sweep" ? "is-sweep" : ""}
    ${isAnalyzing ? "analyzing" : ""}
${showFeedback && !isAnalyzing ? "result" : ""}
  `}
              style={{ width: `${value}%` }}
            />

            {/* SYNC STRIPES ONLY WHILE RECORDING */}
            {mode === "sync" && <div className="tap-prog-sync" aria-hidden />}
          </div>

          {/* FEEDBACK LINK */}
          {showFeedback && (
            <div
              className="tap-feedback-link"
              onClick={onFeedback}
              role="button"
              tabIndex={0}
            >
              Show Feedback
            </div>
          )}
        </div>

        {/* RIGHT CAP */}
        <div className="tap-cap tap-cap-right">{rightCap}</div>
      </div>
    </div>
  );
};

/* ===================================================== */

const TrainingAttemptPlayer: React.FC<{ attemptUuid?: string }> = ({
  attemptUuid,
}) => {
  const params = useParams();
  const [sp] = useSearchParams();
  const uuid = attemptUuid || params.uuid || sp.get("attempt") || "";
  const navigate = useNavigate();
  const atcPlayedRef = useRef(false);

  const [attempt, setAttempt] = useState<AttemptRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [baseImageUrl, setBaseImageUrl] = useState<string | null>(null);
  // below other useState hooks
  const [showResult, setShowResult] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const autoFinalizedRef = useRef(false);
  const [atcMessage, setAtcMessage] = useState<string | null>(null);
  const [showAtc, setShowAtc] = useState(false); // ATC eye: reveal ATC TEXT (no audio)
  const [atcSpeaking, setAtcSpeaking] = useState(false); // Tower: playing ATC AUDIO (no text)

  // **** NEW: timing refs for metrics (monotonic) ****
  const micStartAtRef = useRef<number | null>(null);
  const micStopAtRef = useRef<number | null>(null);
  const slideShownAtRef = useRef<number | null>(performance.now());
  const isFromApp = useRef(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Pinch-to-zoom (mobile / WebView only)
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef<number>(1);
  const zoomScaleRef = useRef<number>(1);
  useEffect(() => {
    zoomScaleRef.current = zoomScale;
  }, [zoomScale]);

  useEffect(() => {
    const isMobileFlag =
      String(attempt?.attempt_snapshot_json?.attempt?.is_mobile) === "Y";
    if (!isMobileFlag) return;
    const el = canvasContainerRef.current;
    if (!el) return;

    const getDist = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchStartDistRef.current = getDist(e.touches);
        pinchStartScaleRef.current = zoomScaleRef.current;
      }
    };

    const onMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchStartDistRef.current != null) {
        e.preventDefault();
        const newDist = getDist(e.touches);
        const ratio = newDist / pinchStartDistRef.current;
        const next = Math.min(
          3,
          Math.max(0.5, pinchStartScaleRef.current * ratio)
        );
        setZoomScale(next);
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinchStartDistRef.current = null;
    };

    el.addEventListener("touchstart", onStart, { passive: false });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);

    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [attempt?.attempt_snapshot_json?.attempt?.is_mobile]);
  //set auth token in localstorage if coming from params

  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("t");
    if (token) {
      isFromApp.current = true;
      localStorage.setItem("access_token", token);
    }
    setTokenReady(true);
  }, []);

  // useEffect(() => {
  //   const token = new URLSearchParams(window.location.search).get("t");
  //   console.log("Token from URL:", token);
  //   if (token) {
  //     isFromApp.current = true;
  //     localStorage.setItem("access_token", token);
  //   }
  // }, []);

  //   const handleFinalizeAndShow = async () => {
  //     try {
  //       window.speechSynthesis.cancel();
  //       setFinalizing(true);
  //       await finalizeAttempt(uuid); // <- your existing finalize call
  //      // await refetch();
  //       setShowResult(true); // <- then show results
  //       setTimeout(() => {
  //   refetch();           // then refresh in background
  // }, 200);
  //     } finally {
  //       setFinalizing(false);
  //     }
  //   };
  const handleFinalizeAndShow = async () => {
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }

      setFinalizing(true);
      await finalizeAttempt(uuid);
      await refetch();
      notifyReactNative("END_EXAM", {
        attempt_uuid: uuid,
      });

      setShowResult(true);
    } finally {
      setFinalizing(false);
    }
  };
  const refetch = useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    setErr(null);
    try {
      const res: any = await WebService.getAPI({
        action: `/training/attempts/${uuid}`,
      });
      setAttempt(res);
      setBaseImageUrl(res?.base_image_url || res?.image_base_url || null);
    } catch (e) {
      setErr("Unable to load the attempt.");
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    if (tokenReady) {
      refetch();
    }
  }, [tokenReady, refetch]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis) return; // 🚫 App WebView doesn't support it

    const load = () => {
      try {
        window.speechSynthesis.getVoices();
      } catch {}
    };

    window.speechSynthesis.onvoiceschanged = load;
    load();

    return () => {
      try {
        window.speechSynthesis.onvoiceschanged = null;
      } catch {}
    };
  }, []);

  const {
    snapshot,
    slides,
    responses,
    idx,
    currentSlide,
    currentResponse,
    hasAnsweredCurrent,
    prev,
    next,
    submitAnswer,
    finalizeAttempt,
  } = useAttemptEngine(attempt, (a) => setAttempt(a), "/training/attempts");

  const totalSlides = slides.length || 0;
  const graded = attempt?.status === "graded";
  const showTimer =
    snapshot?.ui?.show_timer && !!snapshot?.attempt?.timing?.allowed_seconds;
  const timeLeft = useCountdown(
    snapshot?.attempt?.timing?.started_at,
    snapshot?.attempt?.timing?.allowed_seconds || null,
  );

  const [micPhase, setMicPhase] = useState<MicPhase>("idle");

  // RES toggles the footer label only (title <-> "Answer"); stage never shows answers
  const [reveal, setReveal] = useState(false);

  // Check banner
  const [checkMsg, setCheckMsg] = useState<"" | "ok" | "bad">("");
  const [showHelp, setShowHelp] = useState(false);

  // Mic control (voice) + timer
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const recPrev = useRef(recording);
  const recTimerRef = useRef<number | null>(null);

  // sounds
  const nextTone = useClickTone();
  const prevTone = useClickTone();
  console.log("APP STARTED");
  // **** NEW: whenever slide index changes, mark shown time (for latency) ****
  useEffect(() => {
    slideShownAtRef.current = performance.now();
  }, [idx]);
  useEffect(() => {
    setZoomScale(1);
  }, [idx]);
  useEffect(() => {
    const unlockAudio = () => {
      try {
        if (
          typeof window !== "undefined" &&
          window.speechSynthesis &&
          typeof window.speechSynthesis.resume === "function"
        ) {
          window.speechSynthesis.resume();
        }
      } catch {
        /* speechSynthesis not available in this WebView */
      }
    };

    window.addEventListener("click", unlockAudio);
    return () => window.removeEventListener("click", unlockAudio);
  }, []);

  useEffect(() => {
    if (showResult || graded) return;

    const s = currentSlide as any;
    if (!s?.atc?.autoplay || !s?.atc?.text) return;
    if (atcPlayedRef.current) return;

    atcPlayedRef.current = true;

    const text = s.atc.text;
    // Autoplay the ATC AUDIO only. The ATC message TEXT stays hidden until the
    // user clicks ATC / tower — so the slide title shows by default.
    speakSequence([text]);
  }, [currentSlide, showResult, graded]);

  useEffect(() => {
    atcPlayedRef.current = false;
  }, [idx]);

  // useEffect(() => {
  //   if (showResult || graded) return; // ⛔ STOP after submit

  //   const s = currentSlide as any;
  //   if (s?.atc?.autoplay && s?.atc?.text) {
  //     speakSequence([s.atc.text]);
  //   }
  // }, [idx, showResult, graded]);
  useEffect(() => {
    if (!showResult) return;
    if (window.speechSynthesis && !window.ReactNativeWebView) {
      window.speechSynthesis.cancel();
    }

    setAtcMessage(null);
  }, [showResult]);

  useEffect(() => {
    setShowAtc(false);
    setAtcSpeaking(false);
    setAtcMessage(null);
    // RES eye must reset on slide change too — otherwise revealing the answer on
    // one question keeps showing every following question's answer unasked.
    setReveal(false);
  }, [idx]);

  // reset recording if slide is not voice
  useEffect(() => {
    if (currentResponse?.type !== "voice") setRecording(false);
  }, [currentResponse?.type, idx]);

  // timer while recording – start only when mic is truly ready
  useEffect(() => {
    const shouldRun = recording && micPhase === "ready";
    if (shouldRun) {
      setRecSeconds(0);
      recTimerRef.current = window.setInterval(
        () => setRecSeconds((s) => s + 1),
        1000,
      ) as unknown as number;
    } else if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
      if (!recording) setRecSeconds(0);
    }
    return () => {
      if (recTimerRef.current) {
        clearInterval(recTimerRef.current);
        recTimerRef.current = null;
      }
    };
  }, [recording, micPhase]);

  // Auto-finalize when the last slide is answered (no button click needed)
  // useEffect(() => {
  //   const isLast = idx === (slides.length || 0) - 1;
  //   if (!isLast) {
  //     // if the user navigates back, allow auto-finalize again when they return
  //     autoFinalizedRef.current = false;
  //     //  (async () => {
  //     //   await handleFinalizeAndShow();  // your existing finalize -> show modal flow
  //     // })();
  //     return;
  //   }

  //   if (
  //     hasAnsweredCurrent &&
  //     !graded &&
  //     !finalizing &&
  //     !autoFinalizedRef.current &&
  //     uuid
  //   ) {
  //     autoFinalizedRef.current = true; // guard against double fire
  //     (async () => {
  //       await handleFinalizeAndShow(); // your existing finalize -> show modal flow
  //     })();
  //   }
  // }, [idx, slides.length, hasAnsweredCurrent, graded, finalizing, uuid]);
  useEffect(() => {
    console.log("SHOW RESULT CHANGED:", showResult);
  }, [showResult]);
  useEffect(() => {
    const isLast = idx === (slides.length || 0) - 1;
    if (!isLast) {
      autoFinalizedRef.current = false;
      return;
    }

    if (
      // Finalize when the last slide is answered OR when it is an instruction
      // slide the learner has actually navigated to (idx > 0). The idx > 0 guard
      // stops a single-slide / first-slide instruction lesson from finalizing on
      // load (which made the lesson "end immediately" and hid the slide/top bar).
      (hasAnsweredCurrent ||
        (currentSlide?.kind === "instruction" && idx > 0)) &&
      !graded &&
      !finalizing &&
      !autoFinalizedRef.current &&
      uuid
    ) {
      autoFinalizedRef.current = true;
      handleFinalizeAndShow();
    }
  }, [
    idx,
    slides.length,
    hasAnsweredCurrent,
    currentSlide?.kind,
    graded,
    finalizing,
    uuid,
  ]);

  // Navigation rule
  const isInstruction = currentSlide?.kind === "instruction";
  const isLastSlide = idx === (slides.length || 0) - 1;
  // A last-slide instruction has nothing to answer and no "next" to go to. For a
  // single instruction slide (idx 0) the finalize effect intentionally does NOT
  // auto-run on load (that caused "lesson ends immediately"), so without this the
  // learner is left with EVERY control disabled and no way to complete. Let the
  // Next arrow finish the lesson from here instead.
  const canFinishHere =
    isLastSlide && isInstruction && !graded && !finalizing && !!uuid;
  // (nextEnabled is defined further down, after the voice/server_eval state it
  // depends on.)

  // Auto finalize on timer end
  useEffect(() => {
    if (timeLeft === 0 && !graded && uuid) finalizeAttempt(uuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // Only hand a base URL to SVG when media path is NOT absolute (prevents doubling)
  const imageBaseFor = (slide?: Slide | null) => {
    const src = slide?.media?.[0] || "";
    const isAbs = /^https?:\/\//i.test(src);
    return isAbs ? undefined : baseImageUrl || undefined;
  };

  // Resolve base only when path is NOT absolute
  const imageBaseForIndex = (slide?: Slide | null, index: 0 | 1 = 0) => {
    const src = slide?.media?.[index] || "";
    const isAbs = /^https?:\/\//i.test(src);
    return isAbs ? undefined : baseImageUrl || undefined;
  };

  // Build full href for a specific media index (handles absolute vs base+path)
  const imageHrefFor = (slide: Slide, index: 0 | 1) => {
    const m = slide.media?.[index];
    if (!m) return null;
    if (/^https?:\/\//i.test(m)) return m;
    const base = imageBaseForIndex(slide, index);
    // encodeURI so filenames with spaces/special chars resolve (keeps "/").
    return base
      ? `${base.replace(/\/+$/, "")}/${encodeURI(String(m).replace(/^\/+/, ""))}`
      : encodeURI(String(m));
  };

  // Authors sometimes pad a heading with many leading &nbsp; to "center" it by
  // hand. Those non-breaking spaces don't collapse, so the centered line is
  // shoved to one side and can grow wider than the stage (horizontal scroll).
  // Turn &nbsp; into normal spaces for DISPLAY only (stored content untouched) —
  // the browser then collapses the padding and text-align:center does its job.
  const normalizeBodyHtml = (html?: string | null) =>
    (html || "").replace(/(?:&nbsp;| )/gi, " ");

  // Display type
  const displayType = (s?: Slide | null): "image" | "double" | "text" => {
    if (!s) return "image";
    if (s.layout === "image_left") return "double";
    if (s.layout === "image_only") return "text";
    return "image";
  };

  // current answer
  const currentAnswer = useMemo(() => {
    if (!currentResponse || !snapshot) return undefined;
    return snapshot.attempt.answers[currentResponse.id];
  }, [snapshot, currentResponse]);

  // Gate feedback until server evaluation exists
  const isVoice = currentResponse?.type === "voice";

  const hasServerEval = isVoice
    ? !!currentAnswer?.user_response?.server_eval
    : !!currentAnswer;
  // Voice answer saved but no score came back (mic didn't start, upload/scoring
  // failed). Without this the "Analyzing data…" state waited forever.
  const voiceEvalFailed =
    isVoice &&
    hasAnsweredCurrent &&
    !hasServerEval &&
    !recording &&
    micPhase !== "analyzing";
  // While recording or analysing a voice reply the answer isn't final yet.
  const voiceBusy = isVoice && (recording || micPhase === "analyzing");

  // *** IMPORTANT: reuse the SAME condition for disabling "Next" AND for blinking
  // Voice: Next stays off until the score is back — not while recording, not
  // while "Analyzing data…" shows (client: it used to work like this).
  const nextEnabled =
    (idx < (slides.length || 0) - 1 &&
      (isInstruction ||
        graded ||
        (hasAnsweredCurrent && (!isVoice || (hasServerEval && !voiceBusy))))) ||
    canFinishHere;
  // Progress animation state (width transition trigger)
  const [animKey, setAnimKey] = useState(0);
  // Per-slide feedback visibility
  const [feedbackByIndex, setFeedbackByIndex] = useState<
    Record<number, boolean>
  >({});
  // BLINK state (per slide index)
  const [resBlink, setResBlink] = useState<Record<number, boolean>>({});
  const [nextBlink, setNextBlink] = useState<Record<number, boolean>>({});
  // One-shot sweep toggle
  const [sweepOnce, setSweepOnce] = useState(false);
  const SWEEP_MS = 1400;
  const [showFb, setShowFb] = useState(false); // feedback modal

  // Mic readiness phase for accurate timer + UI gating
  type MicPhase = "idle" | "ready" | "analyzing";

  const answeredCount = snapshot?.attempt?.totals?.answered || 0;
  const totalQs = snapshot?.attempt?.totals?.total_questions || 0;
  const answeredPct = percent(answeredCount, Math.max(1, totalQs));
  const scorePct = currentAnswer?.user_response?.server_eval?.score ?? 0;
  console.log("scoreeeeee", scorePct);
  const showFeedbackLink = !!feedbackByIndex[idx];

  const progressValue =
    showFeedbackLink && hasServerEval
      ? scorePct // ✅ actual score
      : answeredPct;

  console.log({
    answeredPct,
    scorePct,
    progressValue,
    showFeedbackLink,
    hasServerEval,
  });
  const revealText = useMemo(
    () => getRevealText(currentResponse as Response | undefined),
    [currentResponse],
  );

  useEffect(() => {
    setAnimKey((k) => k + 1);
  }, [answeredCount]);

  // Voice sweep trigger
  useEffect(() => {
    if (recPrev.current && !recording && isVoice) {
      setSweepOnce(true);
      setAnimKey((k) => k + 1);
      setFeedbackByIndex((m) => ({ ...m, [idx]: true }));
      // blink transition for voice
      flipBlinkToNext(idx);
    }
    recPrev.current = recording;
  }, [recording, isVoice, idx]);

  useEffect(() => {
    initBlinkForSlide(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, graded, hasAnsweredCurrent, nextEnabled]);

  // ---------- Canvas-action state ----------
  const [canvasSelBySlide, setCanvasSelBySlide] = useState<
    Record<string, string[]>
  >({});
  const [canvasSubmittedBySlide, setCanvasSubmittedBySlide] = useState<
    Record<string, boolean>
  >({});
  const [allowBlurBySlide, setAllowBlurBySlide] = useState<
    Record<string, boolean>
  >({});
  const [pendingBlurSlideId, setPendingBlurSlideId] = useState<string | null>(
    null,
  );

  const curSlideId = currentSlide?.id || "";
  const currentSel = canvasSelBySlide[curSlideId] || [];
  const canvasSubmitted = !!canvasSubmittedBySlide[curSlideId];
  const allowBlur = !!allowBlurBySlide[curSlideId];

  // progress mode selection
  const progMode: ProgMode =
    isVoice && recording ? "sync" : sweepOnce ? "sweep" : "idle";

  const afterProgressAnim = () => {
    if (sweepOnce) setSweepOnce(false);
    if (pendingBlurSlideId) {
      setAllowBlurBySlide((m) => ({ ...m, [pendingBlurSlideId]: true }));
      setPendingBlurSlideId(null);
    }
  };

  const afterAnyAnswer = () => {
    setFeedbackByIndex((m) => ({ ...m, [idx]: true }));
    setSweepOnce(true);
    setAnimKey((k) => k + 1);
    // blink transition
    flipBlinkToNext(idx);
  };

  // submit handlers (non-canvas)
  const handleSelect = (r: ResponseSelect, optId: string) => {
    submitAnswer(uuid, r.id, { selected: [optId] });
    afterAnyAnswer();
  };
  const handleNumeric = (r: ResponseNumeric, value: number | null) => {
    submitAnswer(uuid, r.id, { value });
    afterAnyAnswer();
  };
  const handleText = (r: ResponseText, text: string) => {
    submitAnswer(uuid, r.id, { text });
    afterAnyAnswer();
  };

  /* ---------- upload helper for voice blob ---------- */
  const uploadAttemptFile = async (file: Blob, name = "recording.mp3") => {
    try {
      const fd = new FormData();
      fd.append("file", file, name);
      const res: any = await WebService.fileUploadAPI({
        action: `/training/attempts/${uuid}/upload-voice`,
        body: fd,
      });
      return res?.url || res?.path || "";
    } catch (e) {
      console.error("uploadAttemptFile error", e);
    }
  };

  // REPLACE the whole handleVoice with this version
  const handleVoice = async (
    r: ResponseVoice,
    payload: {
      url?: string;
      server_text?: string; // <-- from VoiceWidget (core/transcript result.text)
      server_eval?: any; // <-- from VoiceWidget (core/transcript result.evaluation)
    },
  ) => {
    // **** compute speaking + response times using monotonic clock ****
    const now = performance.now();

    const speakingMs =
      micStartAtRef.current != null
        ? Math.max(
            0,
            Math.round((micStopAtRef.current ?? now) - micStartAtRef.current),
          )
        : recSeconds > 0
          ? recSeconds * 1000
          : undefined;

    const responseMs =
      slideShownAtRef.current != null && micStartAtRef.current != null
        ? Math.max(
            0,
            Math.round(micStartAtRef.current - slideShownAtRef.current),
          )
        : undefined;

    await submitAnswer(uuid, r.id, {
      url: payload.url,
      transcript: payload.server_text, // store for convenience
      server_text: payload.server_text, // FeedbackModal reads this
      server_eval: payload.server_eval, // FeedbackModal reads this

      // timings for server rollup
      voice_dur_ms: typeof speakingMs === "number" ? speakingMs : undefined,
      latency_ms: typeof responseMs === "number" ? responseMs : undefined,
    });

    // Optional: clear for next answer cycle (doesn't change any other logic)
    micStartAtRef.current = null;
    micStopAtRef.current = null;
  };

  /** Initialize blink for current slide — use EXACT same condition as "Next" disabled */
  const initBlinkForSlide = (slideIdx: number) => {
    setResBlink((m) => ({ ...m, [slideIdx]: !graded && !hasAnsweredCurrent }));
    setNextBlink((m) => ({
      ...m,
      [slideIdx]: !graded && hasAnsweredCurrent && nextEnabled,
    }));
  };

  /** Flip from RES blinking -> NEXT blinking (call after user answers/stops voice) */
  const flipBlinkToNext = (slideIdx: number) => {
    setResBlink((m) => ({ ...m, [slideIdx]: false }));
    setNextBlink((m) => ({ ...m, [slideIdx]: nextEnabled }));
  };

  // canvas helpers
  const setCanvasSelected = (slideId: string, id: string) => {
    setCanvasSelBySlide((m) => ({ ...m, [slideId]: [id] })); // single select
  };

  const submitCanvasNow = (r: ResponseCanvas, slideId: string) => {
    const sel = canvasSelBySlide[slideId] || [];
    if (!sel.length) return;

    submitAnswer(uuid, r.id, { selected: sel });
    setCanvasSubmittedBySlide((m) => ({ ...m, [slideId]: true }));

    setSweepOnce(true);
    setAnimKey((k) => k + 1);
    setPendingBlurSlideId(slideId);

    setFeedbackByIndex((m) => ({ ...m, [idx]: true }));
  };
  // Show the ATC script whenever the slide actually has script text — same as the
  // author/editor. (Was gated on "no media", which hid the script on Text-layout
  // question slides that carry a placeholder media entry, e.g. slide 7.)
  const shouldShowScript = (slide?: Slide | null) => {
    const script = (slide as any)?.script;
    return (
      Array.isArray(script) && script.some((l: any) => (l?.text || "").trim())
    );
  };

  const doCheck = () => {
    const isCorrect = currentAnswer?.auto_grade?.is_correct;
    // setCheckMsg(isCorrect ? "ok" : "bad");
    window.setTimeout(() => setCheckMsg(""), 1200);
    setShowHelp(true);
  };
  const helpData = useMemo(() => {
    if (!currentSlide || currentSlide.kind !== "question") return null;

    const s: any = currentSlide;
    console.log("slidesssssssssss", s);
    const scriptMessages = (s.script || [])
      .filter((l: any) => l.message && l.message.trim())
      .map((l: any) => ({
        label: l.message, // LEFT
        value: l.text, // RIGHT (🔥 missing earlier)
      }));

    const lastAtcSlide = [...slides]
      .reverse()
      .find(
        (sl: any) =>
          sl.kind === "instruction" &&
          sl.atc &&
          typeof sl.atc.text === "string" &&
          sl.atc.text.trim().length > 0,
      );

    //  const atcInstruction = (lastAtcSlide as any)?.atc?.text || "";
    // 1️⃣ Prefer ATC from CURRENT slide (question)
    const atcInstruction =
      typeof s.atc?.text === "string" && s.atc.text.trim().length > 0
        ? s.atc.text
        : (() => {
            // 2️⃣ Fallback: last instruction slide with ATC
            const lastAtcSlide = [...slides]
              .reverse()
              .find(
                (sl: any) =>
                  sl.kind === "instruction" &&
                  typeof sl.atc?.text === "string" &&
                  sl.atc.text.trim().length > 0,
              );

            return (lastAtcSlide as any)?.atc?.text || "";
          })();

    console.log("ggg", atcInstruction);
    const expectedResponse = getRevealText(
      currentResponse as Response | undefined,
    );

    return {
      title: s.title || "",
      scriptMessages,
      atcInstruction,
      expectedResponse,
    };
  }, [currentSlide, currentResponse]);

  /* --------------- renderers (stage never shows answers) --------------- */
  const renderInstruction = (slide: Slide) => {
    const dt = displayType(slide);

    if (dt === "text") {
      // a) If we have an image, keep existing path
      if (slide.media?.[0]) {
        return (
          <div
            className="image-preview-container"
            style={{ width: "100%", height: "100%", position: "relative" }}
          >
            <SVGRender
              slide={slide}
              imageBaseUrl={imageBaseFor(slide)}
              showScriptOverlay={shouldShowScript(slide)}
              autoplayScript={false}
              radioSpeak={(text) => speakSequence([text])}
            />
          </div>
        );
      }

      // b) If we have body_html, keep existing path
      if (slide.body_html) {
        return (
          <div
            className="tap-textblock rendered-text"
            dangerouslySetInnerHTML={{ __html: normalizeBodyHtml(slide.body_html) }}
          />
        );
      }

      // c) NEW: script-only (no media, no body_html) -> mount blank SVG host so overlay shows
      const script = (slide as any).script as
        | Array<{ text?: string }>
        | undefined;
      const hasScript =
        (!slide.media || slide.media.length === 0) &&
        Array.isArray(script) &&
        script.some((l) => (l.text || "").trim());

      if (hasScript) {
        return (
          <div
            className="image-preview-container"
            style={{ width: "100%", height: "100%", position: "relative" }}
          >
            <SVGRender
              slide={slide}
              imageBaseUrl={undefined}
              showScriptOverlay={shouldShowScript(slide)}
              autoplayScript={false}
              radioSpeak={(text) => speakSequence([text])}
            />
          </div>
        );
      }

      // d) nothing to render
      return null;
    }

    if (dt === "image") {
      return slide.media?.[0] ? (
        <div
          className="image-preview-container"
          style={{ width: "100%", height: "100%", position: "relative" }}
        >
          <SVGRender
            slide={slide}
            imageBaseUrl={imageBaseForIndex(slide, 0)}
            showScriptOverlay={shouldShowScript(slide)}
            autoplayScript={false}
            radioSpeak={(text) => speakSequence([text])}
          />
        </div>
      ) : null;
    }

    // DOUBLE
    return (
      <div className="double-image-wrap">
        <div className="half">
          {slide.media?.[0] ? (
            <div
              className="image-preview-container"
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <SVGRender
                slide={slide}
                imageBaseUrl={imageBaseForIndex(slide, 0)}
                showScriptOverlay={shouldShowScript(slide)}
                autoplayScript={false}
                radioSpeak={(text) => speakSequence([text])}
              />
            </div>
          ) : null}
        </div>
        <div className="half">
          {slide.media?.[1] ? (
            <div
              className="image-preview-container"
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <img
                className="preview-image contain"
                src={imageHrefFor(slide, 1)!}
                alt="Reference"
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderQuestion = (slide: Slide) => {
    const r = currentResponse as Response | undefined;

    const ans = currentAnswer?.user_response;
    const selected = (ans?.selected as string[]) || [];
    const numVal = ans?.value ?? null;
    const txtVal = ans?.text ?? "";
    const voiceVal = ans ? { transcript: ans.transcript, url: ans.url } : null;
    const dt = displayType(slide);
    const isCanvasQ = r?.type === "canvas_action";

    return (
      <>
        {/* IMAGE / DOUBLE when NOT text-only width: "100%", height: "100%", */}
        {dt !== "text" && (
          <>
            {dt === "image" && slide.media?.[0] && (
              <div
                className="image-preview-container"
                style={{ position: "relative" }}
              >
                <SVGRender
                  slide={slide}
                  imageBaseUrl={imageBaseForIndex(slide, 0)}
                  isPreview={isCanvasQ}
                  response={isCanvasQ ? (r as ResponseCanvas) : undefined}
                  selectedIds={isCanvasQ ? currentSel : undefined}
                  allowBlur={isCanvasQ ? allowBlur : false}
                  onPlaneSelect={
                    isCanvasQ
                      ? (id) => setCanvasSelected(slide.id, id)
                      : undefined
                  }
                  showScriptOverlay={shouldShowScript(slide)}
                  autoplayScript={false}
                  radioSpeak={(text) => speakSequence([text])}
                />
              </div>
            )}

            {dt === "double" && (
              <div className="double-image-wrap">
                {/* LEFT: interactive SVG on media[0] */}
                <div className="half">
                  {slide.media?.[0] && (
                    <div
                      className="image-preview-container"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      <SVGRender
                        slide={slide}
                        imageBaseUrl={imageBaseForIndex(slide, 0)}
                        isPreview={isCanvasQ}
                        response={isCanvasQ ? (r as ResponseCanvas) : undefined}
                        selectedIds={isCanvasQ ? currentSel : undefined}
                        allowBlur={isCanvasQ ? allowBlur : false}
                        onPlaneSelect={
                          isCanvasQ
                            ? (id) => setCanvasSelected(slide.id, id)
                            : undefined
                        }
                        showScriptOverlay={shouldShowScript(slide)}
                        autoplayScript={false}
                        radioSpeak={(text) => speakSequence([text])}
                      />
                    </div>
                  )}
                </div>

                {/* RIGHT: plain preview on media[1] */}
                <div className="half">
                  {slide.media?.[1] && (
                    <div
                      className="image-preview-container"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      <img
                        className="preview-imagewithoutborder contain"
                        src={imageHrefFor(slide, 1)!}
                        alt="Reference"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* for ATC Script — patched to support script-only on text layout */}
        {dt === "text" &&
          (() => {
            const script = (slide as any).script as
              | Array<{ text?: string }>
              | undefined;
            const hasScript =
              (!slide.media || slide.media.length === 0) &&
              Array.isArray(script) &&
              script.some((l) => (l.text || "").trim());

            // body_html can be "empty" HTML like "<p><br></p>" — strip tags &
            // whitespace to detect whether there's REAL text. Otherwise the
            // empty paragraph was treated as content and hid the ATC script.
            const hasRealBody =
              ((slide.body_html || "") as string)
                .replace(/<[^>]*>/g, "")
                .replace(/&nbsp;/gi, " ")
                .replace(/ /g, " ")
                .trim().length > 0;

            // 1) If we have an image, keep existing path
            if (slide.media?.[0]) {
              return (
                <div
                  className="image-preview-container"
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <SVGRender
                    slide={slide}
                    imageBaseUrl={imageBaseForIndex(slide, 0)}
                    isPreview={isCanvasQ}
                    response={isCanvasQ ? (r as ResponseCanvas) : undefined}
                    selectedIds={isCanvasQ ? currentSel : undefined}
                    allowBlur={isCanvasQ ? allowBlur : false}
                    onPlaneSelect={
                      isCanvasQ
                        ? (id) => setCanvasSelected(slide.id, id)
                        : undefined
                    }
                    showScriptOverlay={shouldShowScript(slide)}
                    autoplayScript={false}
                    radioSpeak={(text) => speakSequence([text])}
                  />
                </div>
              );
            }

            // 2) If no image but REAL body text, show text as before.
            //    (Empty "<p><br></p>" no longer counts, so the script can show.)
            if (hasRealBody) {
              return (
                <div
                  className="tap-textblock rendered-text"
                  dangerouslySetInnerHTML={{ __html: normalizeBodyHtml(slide.body_html) }}
                />
              );
            }

            // 3) NEW: script-only (no media, no body_html) -> mount blank SVG host so overlay shows
            if (hasScript) {
              return (
                <div
                  className="image-preview-container"
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <SVGRender
                    slide={slide}
                    imageBaseUrl={undefined}
                    isPreview={isCanvasQ}
                    response={isCanvasQ ? (r as ResponseCanvas) : undefined}
                    selectedIds={isCanvasQ ? currentSel : undefined}
                    allowBlur={isCanvasQ ? allowBlur : false}
                    onPlaneSelect={
                      isCanvasQ
                        ? (id) => setCanvasSelected(slide.id, id)
                        : undefined
                    }
                    showScriptOverlay={shouldShowScript(slide)}
                    autoplayScript={false}
                    radioSpeak={(text) => speakSequence([text])}
                  />
                </div>
              );
            }

            // 4) nothing to render
            return null;
          })()}

        {/* Widgets */}
        {r?.type === "select" && (
          <SelectWidget
            response={r as ResponseSelect}
            disabled={graded}
            currentSelected={selected}
            onSelect={(id) => handleSelect(r as ResponseSelect, id)}
            reveal={false}
          />
        )}

        {r?.type === "numeric" && (
          <NumericWidget
            response={r as ResponseNumeric}
            disabled={graded}
            value={numVal}
            onCommit={(v) => handleNumeric(r as ResponseNumeric, v)}
            reveal={false}
          />
        )}

        {r?.type === "text" && (
          <TextWidget
            response={r as ResponseText}
            disabled={graded}
            value={txtVal}
            onCommit={(t) => handleText(r as ResponseText, t)}
            reveal={false}
          />
        )}

        {r?.type === "voice" && (
          <VoiceWidget
            response={r as ResponseVoice}
            disabled={graded}
            value={voiceVal}
            onCommit={(p) => handleVoice(r as ResponseVoice, p)}
            reveal={false}
            controlledRecording={recording}
            attemptUuid={uuid}
            onMicState={(state) => {
              if (state === "ready") {
                setMicPhase("ready");
                micStartAtRef.current = performance.now();
                setRecSeconds(0);
                if (!recTimerRef.current) {
                  recTimerRef.current = window.setInterval(
                    () => setRecSeconds((s) => s + 1),
                    1000,
                  ) as unknown as number;
                }
              } else if (state === "stopped") {
                setMicPhase("idle");
                micStopAtRef.current = performance.now();
                if (recTimerRef.current) {
                  clearInterval(recTimerRef.current);
                  recTimerRef.current = null;
                  setRecSeconds(0);
                }
              } else if (state === "analyzing") {
                setMicPhase("analyzing");
              }
            }}
          />
        )}
      </>
    );
  };

  /* --------------- early returns --------------- */
  if (!uuid) return <Alert variant="warning">No attempt specified.</Alert>;
  if (loading) {
    return (
      <div className="tap-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading attempt…
      </div>
    );
  }
  if (err || !attempt || !snapshot) {
    return (
      <div className="tap-center">
        <Alert variant="danger">{err || "Attempt not found."}</Alert>
        <Button size="sm" variant="outline-secondary" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  const atcEnabled = hasAtc(currentSlide);
  //const showFeedbackLink = !!feedbackByIndex[idx];
  const isCanvasQ = currentResponse?.type === "canvas_action";

  const zoomIn = () => setZoomScale((z) => Math.min(z + 0.2, 3)); // max 3x

  const zoomOut = () => setZoomScale((z) => Math.max(z - 0.2, 0.5)); // min 0.5x
  const courseLessonUuid = snapshot?.attempt.course_lesson_uuid;
  console.log("lessonuuid", courseLessonUuid);

  const resetZoom = () => setZoomScale(1);
  const dt = displayType(currentSlide);
  // Zoom is for images only. (dt can't decide this: an "image_only" layout maps to
  // dt "text", which hid the buttons on exactly the slides that are just an image.)
  const showZoomControls = !!currentSlide?.media?.some(Boolean);
  const isResultMode = showFeedbackLink && hasServerEval;
  const runtimeParams = snapshot?.runtime_params || {};
  // Per-page bar: when the lesson enables "Different bar content per page", each
  // slide carries its own bar config (slide.info_bar); otherwise the global
  // runtime_params bar is used for every slide.
  const perPageBar = (runtimeParams as any).per_page_bar;
  const barHasContent = (b: any) =>
    !!(b && (b.call_sign || b.aircraft_type || b.atis || b.altitude));
  // Per-page mode: use THIS slide's bar; if this slide has none, fall back to the
  // first slide that does (then the global bar) so a top bar the author set only
  // on one page still shows on every page, unless a page overrides it.
  const firstConfiguredBar = perPageBar
    ? (slides || []).map((s: any) => s?.info_bar).find(barHasContent)
    : null;
  const barCfg: any = perPageBar
    ? (barHasContent((currentSlide as any)?.info_bar)
        ? (currentSlide as any).info_bar
        : firstConfiguredBar || runtimeParams)
    : runtimeParams;

  const callSign = barCfg.call_sign;
  const atis = barCfg.atis;
  const altitude = barCfg.altitude;
  /* --------------- UI --------------- */
  console.log("plot", snapshot?.runtime_params);
  const isMobile =
    String(attempt?.attempt_snapshot_json?.attempt?.is_mobile) === "Y";
  const learningMode =
    (attempt?.attempt_snapshot_json as any)?.attempt?.learning_mode || null;
  const showTrainingDisclaimer = learningMode === "NORMAL";
  // Gate check = a test (lesson's level is a stage checker, set by the backend):
  // the "cheat" helpers — ATC eye (ATC text), RES (expected answer) and Help —
  // stay visible but disabled. The tower (hear ATC audio) still works: it's the
  // transmission the student has to answer.
  const isGateCheck = !!(attempt?.attempt_snapshot_json as any)?.attempt
    ?.is_gate_check;
  // CALL SIGN / ATIS / ALTITUDE bar belongs to the flight simulator only —
  // never show it in a normal training lesson.
  const attemptMode =
    (attempt as any)?.mode ||
    (attempt?.attempt_snapshot_json as any)?.attempt?.mode;
  const isSimulator =
    attemptMode === "simulator" || learningMode === "SIMULATOR_SET";
  // Simulator shows ATIS "A" by default; training shows whatever the editor set.
  const atisDisplay = isSimulator ? barCfg.atis || "A" : barCfg.atis;
  return (
    <div className={`tap-shell${isMobile ? "" : " tap-shell--site"}`}>
      {/* top-right close (X) — same logic as the old Back button */}
      <div className="tap-topbar">
        {!isMobile && (
          <button
            className="tap-closebtn"
            aria-label="Close"
            title="Close"
            onClick={() => {
              const attemptMode =
                attempt?.mode ||
                (attempt?.attempt_snapshot_json as any)?.attempt?.mode;
              console.log("🔙 Back clicked. attempt.mode =", attemptMode);
              navigate(
                attemptMode === "simulator"
                  ? "/flight"
                  : `/training/lesson/detail/${courseLessonUuid}`
              );
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>

      {/* Info bar (light) — CALL SIGN | ATIS | ALTITUDE | TYPE.
          Shown in BOTH simulator and training (client: the top bar we show in the
          simulator must also show in training). Config comes from barCfg (global,
          or per-slide when the lesson enables "Different bar content per page"). */}
      {(isSimulator ||
        barCfg.show_info_bar ||
        barCfg.call_sign ||
        barCfg.aircraft_type ||
        atisDisplay ||
        barCfg.altitude) && (
        <div className="tap-infobar">
          {/* Three FIXED boxes, each exactly 1/3 wide — never move/resize/re-flow.
              A value that is off just leaves its box (or field) blank. */}

          {/* LEFT box — CALL SIGN + TYPE. Both set = left-aligned (call sign first,
              type second); only one set = centred in the box. */}
          <div
            className={`info-box info-box-left${
              barCfg.call_sign && barCfg.aircraft_type ? " has-two" : ""
            }`}
          >
            {barCfg.call_sign && (
              <div className="info-cell">
                <span className="info-cell-label">CALL SIGN</span>
                <span className="info-cell-value">{barCfg.call_sign}</span>
              </div>
            )}
            {barCfg.aircraft_type && (
              <div className="info-cell">
                <span className="info-cell-label">TYPE</span>
                <span className="info-cell-value">{barCfg.aircraft_type}</span>
              </div>
            )}
          </div>

          {/* MIDDLE box — ATIS */}
          <div className="info-box">
            {atisDisplay && (
              <div className="info-cell">
                <span className="info-cell-label">ATIS</span>
                <span className="info-cell-value">{atisDisplay}</span>
              </div>
            )}
          </div>

          {/* RIGHT box — ALTITUDE */}
          <div className="info-box">
            {barCfg.altitude && (
              <div className="info-cell">
                <span className="info-cell-label">ALTITUDE</span>
                <span className="info-cell-value">{barCfg.altitude}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* stage */}
      <div
        className="canvas-container"
        ref={canvasContainerRef}
        style={isMobile ? { touchAction: "none" } : undefined}
      >
        {/* --zoom: on desktop .canvas-stage is display:contents (no box), so its
            own transform is ignored and the CSS scales this wrapper instead. */}
        <div
          className="canvas-stage-zoom"
          style={{ "--zoom": zoomScale } as React.CSSProperties}
        >
          <div
            className="canvas-stage"
            style={{
              transform: `scale(${zoomScale})`,
              transformOrigin: "center center",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            {currentSlide?.kind === "instruction"
              ? renderInstruction(currentSlide)
              : renderQuestion(currentSlide as Slide)}
          </div>
        </div>
        {showZoomControls && (
          <div className="zoom-controls">
            <button onClick={zoomIn}>＋</button>
            <button onClick={zoomOut}>−</button>
            {/* <button onClick={resetZoom}>⟳</button> */}
          </div>
        )}

        {showTrainingDisclaimer && (
          <div className="training-disclaimer" role="note">
            For training purposes only. Not to be used for flight.
          </div>
        )}
      </div>

      {/* footer progress + controls */}
      <div className="canvas-footer">
        <div className="footer-progress top-progress">
          {/* Full-width bar (original layout) with counter (left) + score (right) caps */}
          <ProgressSingleForTap
            value={isResultMode ? scorePct : 100}
            animateTrigger={animKey}
            mode={progMode}
            sweepMs={SWEEP_MS}
            onAfterAnimate={afterProgressAnim}
            showFeedback={showFeedbackLink && hasServerEval}
            onFeedback={() => setShowFb(true)}
            isAnalyzing={showFeedbackLink && !hasServerEval && !voiceEvalFailed}
            leftCap={`${idx + 1}/${totalSlides}`}
            rightCap={isResultMode ? `${Math.round(scorePct)}/100` : "100"}
          />
          {/* {showFeedbackLink && !isGraded && !showResult && (
            <div className="tap-feedback-link muted">Analyzing data…</div>
          )} */}

          {currentResponse?.type === "voice" &&
            showFeedbackLink &&
            !hasServerEval &&
            !voiceEvalFailed && (
              <div className="tap-feedback-link muted">Analyzing data…</div>
            )}
          {voiceEvalFailed && showFeedbackLink && (
            <div className="tap-feedback-link">
              Couldn't analyse your answer — please record again.
            </div>
          )}

          {/* PLACE SUBMIT BUTTON **BELOW** FEEDBACK LINK (inside footer-progress), ONLY for canvas_action */}
          {isCanvasQ && !graded && (
            <div
              className="d-flex justify-content-center my-1"
              style={{ width: "100%" }}
            >
              <Button
                variant="primary"
                onClick={() =>
                  submitCanvasNow(
                    currentResponse as ResponseCanvas,
                    currentSlide!.id,
                  )
                }
                disabled={!currentSel.length || canvasSubmitted}
                style={{ minWidth: 220, height: 40, fontWeight: 700 }}
              >
                {canvasSubmitted ? "Submitted" : "Submit"}
              </Button>
            </div>
          )}

        </div>

        {/* ===== Prompt row: tower (hear ATC) + ATC text + mic (record) ===== */}
        <div className="tap-prompt-row">
          {/* Tower = hear ATC (same handler as old ATC button) */}
          <button
            className={`tap-tower-btn ${atcSpeaking ? "is-speaking" : ""}`}
            onClick={() => {
              if (!atcEnabled) return;

              // Tower = HEAR ATC only (audio). Text reveal is the ATC eye's job.
              // Second click stops the voice.
              if (atcSpeaking) {
                setAtcSpeaking(false);
                if (window.speechSynthesis && !window.ReactNativeWebView) {
                  window.speechSynthesis.cancel();
                }
                return;
              }

              const s = currentSlide;
              if (!s) return;

              // Build the line(s) to speak (do NOT show the text here)
              let lines: string[] | null = null;
              if ((s as any).atc?.text) {
                lines = [(s as any).atc.text];
              } else {
                const script = (s as any).script as
                  | Array<{ text: string; tts?: boolean }>
                  | undefined;
                if (script?.length) {
                  lines = script.some((l) => l.tts)
                    ? script.filter((l) => l.tts).map((l) => l.text)
                    : script.map((l) => l.text);
                } else if (s.title) {
                  lines = [s.title];
                }
              }

              if (!lines?.length) return;

              setAtcSpeaking(true);
              // Clear the "speaking" state once the transmission finishes
              Promise.resolve(speakSequence(lines)).finally(() =>
                setAtcSpeaking(false),
              );
            }}
            disabled={!atcEnabled}
            title="Hear ATC"
            aria-label="Hear ATC"
          >
            <svg
              viewBox="0 0 92 169"
              width="28"
              height="50"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M31 165.5C31 165.06 30.94 165 30.5 165C30.06 165 30 164.94 30 164.5C30 164.06 29.94 164 29.5 164C29 164 29 164 29 138C29 112 29 112 28.5 112C28 112 28 112 28 109C28 106 28 106 27.5 106C27 106 27 106 27 103.5L27 101 30 101L33 101 33 103.5C33 106 33 106 33.5 106C34 106 34 106 34 109.5C34 113 34 113 34.5 113C35 113 35 113 35 136.5L35 160 46.5 160L58 160 58 137C58 114 58 114 58.5 114C59 114 59 114 59 112.5C59 111 59 111 59.5 111C60 111 60 111 60 109.5C60 108 60 108 60.5 108C61 108 61 108 61 106.5C61 105 61 105 61.5 105C62 105 62 105 62 103.5C62 102 62 102 62.5 102C63 102 63 102 63 100.5C63 99 63 99 63.5 99C64 99 64 99 64 97.5L64 96 38 96L12 96 12 95C12 94 12 94 11.5 94C11 94 11 94 11 92.5C11 91 11 91 10.5 91C10 91 10 91 10 89C10 87 10 87 9.5 87C9 87 9 87 9 85.5C9 84 9 84 8.5 84C8 84 8 84 8 82C8 80 8 80 7.5 80C7 80 7 80 7 78.5C7 77 7 77 6.5 77C6 77 6 77 6 75C6 73 6 73 5.5 73C5 73 5 73 5 71.5C5 70 5 70 4.5 70C4 70 4 70 4 68C4 66 4 66 4.5 66C4.94 66 5 65.94 5 65.5C5 65 5 65 46.5 65C88 65 88 65 88 65.5C88 65.94 88.06 66 88.5 66C89 66 89 66 89 67.5C89 69 89 69 88.5 69C88 69 88 69 88 71C88 73 88 73 87.5 73C87 73 87 73 87 74.5C87 76 87 76 86.5 76C86 76 86 76 86 78C86 80 86 80 85.5 80C85 80 85 80 85 81.5C85 83 85 83 84.5 83C84 83 84 83 84 85C84 87 84 87 83.5 87C83 87 83 87 83 88C83 89 83 89 82 89C81 89 81 89 81 89.5C81 90 81 90 79 90L77 90 77 88C77 86 77 86 77.5 86C78 86 78 86 78 84.5C78 83 78 83 78.5 83C79 83 79 83 79 81C79 79 79 79 79.5 79C80 79 80 79 80 77.5C80 76 80 76 80.5 76C81 76 81 76 81 74C81 72 81 72 81.5 72C81.94 72 82 71.94 82 71.5C82 71 82 71 81 71C80 71 80 71 80 70.5C80 70 80 70 79 70C78 70 78 70 78 70.5C78 71 78 71 45.5 71C13 71 13 71 13 70.5C13 70.06 12.94 70 12.5 70C12.06 70 12 70.06 12 70.5C12 70.94 11.94 71 11.5 71C11 71 11 71 11 72C11 73 11 73 11.5 73C12 73 12 73 12 75C12 77 12 77 12.5 77C13 77 13 77 13 78.5C13 80 13 80 13.5 80C14 80 14 80 14 82C14 84 14 84 14.5 84C15 84 15 84 15 85.5C15 87 15 87 15.5 87C16 87 16 87 16 88.5C16 90 16 90 17 90C18 90 18 90 18 90.5C18 90.94 18.06 91 18.5 91C18.94 91 19 90.94 19 90.5C19 90 19 90 44 90C69 90 69 90 69 90.5C69 91 69 91 70 91C71 91 71 91 71 93.5C71 96 71 96 70.5 96C70 96 70 96 70 97.5C70 99 70 99 69.5 99C69 99 69 99 69 100.5C69 102 69 102 68.5 102C68 102 68 102 68 103.5C68 105 68 105 67.5 105C67 105 67 105 67 106.5C67 108 67 108 66.5 108C66 108 66 108 66 109.5C66 111 66 111 65.5 111C65 111 65 111 65 112.5C65 114 65 114 64.5 114C64 114 64 114 64 116C64 118 64 118 63.5 118C63 118 63 118 63 141.5C63 165 63 165 62.5 165C62.06 165 62 165.06 62 165.5C62 166 62 166 46.5 166C31 166 31 166 31 165.5ZM17 58.5C17 58.06 16.94 58 16.5 58C16 58 16 58 16 57C16 56 16 56 16.5 56C17 56 17 56 17 54.5C17 53 17 53 17.5 53C18 53 18 53 18 51C18 49 18 49 18.5 49C19 49 19 49 19 47.5C19 46 19 46 19.5 46C20 46 20 46 20 45C20 44 20 44 20.5 44C20.94 44 21 43.94 21 43.5C21 43.06 21.06 43 21.5 43C21.94 43 22 42.94 22 42.5C22 42 22 42 23 42C24 42 24 42 24 41.5C24 41 24 41 33.5 41L43 41 43 32.5C43 24 43 24 42.5 24C42.06 24 42 23.94 42 23.5C42 23.06 41.94 23 41.5 23C41 23 41 23 41 22C41 21 41 21 40.5 21C40 21 40 21 40 18.5C40 16 40 16 40.5 16C40.94 16 41 15.94 41 15.5C41 15.06 41.06 15 41.5 15C41.94 15 42 14.94 42 14.5C42 14.06 42.06 14 42.5 14C42.94 14 43 13.94 43 13.5C43 13 43 13 44 13C45 13 45 13 45 12.5C45 12 45 12 46.5 12C48 12 48 12 48 12.5C48 13 48 13 49 13C50 13 50 13 50 13.5C50 13.94 50.06 14 50.5 14C50.94 14 51 14.06 51 14.5C51 14.94 51.06 15 51.5 15C52 15 52 15 52 16C52 17 52 17 52.5 17C53 17 53 17 53 19C53 21 53 21 52.5 21C52 21 52 21 52 22C52 23 52 23 51.5 23C51.06 23 51 23.06 51 23.5C51 23.94 50.94 24 50.5 24C50.06 24 50 24.06 50 24.5C50 24.94 49.94 25 49.5 25C49 25 49 25 49 33L49 41 59 41C69 41 69 41 69 41.5C69 42 69 42 70 42C71 42 71 42 71 42.5C71 42.94 71.06 43 71.5 43C71.94 43 72 43.06 72 43.5C72 43.94 72.06 44 72.5 44C72.94 44 73 44.06 73 44.5C73 44.94 73.06 45 73.5 45C74 45 74 45 74 47C74 49 74 49 74.5 49C75 49 75 49 75 51C75 53 75 53 75.5 53C76 53 76 53 76 56L76 59 73.5 59C71 59 71 59 71 58C71 57 71 57 70.5 57C70 57 70 57 70 55C70 53 70 53 69.5 53C69 53 69 53 69 51C69 49 69 49 68.5 49C68 49 68 49 68 48L68 47 46.5 47L25 47 25 48C25 49 25 49 24.5 49C24 49 24 49 24 51C24 53 24 53 23.5 53C23 53 23 53 23 54.5C23 56 23 56 22.5 56C22 56 22 56 22 57.5L22 59 19.5 59C17 59 17 59 17 58.5ZM33 32.5C33 32.06 32.94 32 32.5 32C32.06 32 32 31.94 32 31.5C32 31.06 31.94 31 31.5 31C31.06 31 31 30.94 31 30.5C31 30.06 30.94 30 30.5 30C30.06 30 30 29.94 30 29.5C30 29.06 29.94 29 29.5 29C29 29 29 29 29 27.5C29 26 29 26 28.5 26C28 26 28 26 28 24.5C28 23 28 23 27.5 23C27 23 27 23 27 18.5C27 14 27 14 27.5 14C28 14 28 14 28 12.5C28 11 28 11 28.5 11C29 11 29 11 29 10C29 9 29 9 29.5 9C29.94 9 30 8.94 30 8.5C30 8.06 30.06 8 30.5 8C31 8 31 8 31 7C31 6 31 6 31.5 6C31.94 6 32 5.94 32 5.5C32 5.06 32.06 5 32.5 5C32.94 5 33 4.94 33 4.5C33 4 33 4 34.5 4C36 4 36 4 36 4.5C36 4.94 36.06 5 36.5 5C36.94 5 37 5.06 37 5.5C37 5.94 37.06 6 37.5 6C38 6 38 6 38 7C38 8 38 8 37.5 8C37 8 37 8 37 9C37 10 37 10 36.5 10C36.06 10 36 10.06 36 10.5C36 10.94 35.94 11 35.5 11C35.06 11 35 11.06 35 11.5C35 11.94 34.94 12 34.5 12C34 12 34 12 34 13.5C34 15 34 15 33.5 15C33 15 33 15 33 19C33 23 33 23 33.5 23C34 23 34 23 34 24C34 25 34 25 34.5 25C35 25 35 25 35 26C35 27 35 27 35.5 27C35.94 27 36 27.06 36 27.5C36 27.94 36.06 28 36.5 28C37 28 37 28 37 29C37 30 37 30 37.5 30C38 30 38 30 38 31C38 32 38 32 37.5 32C37.06 32 37 32.06 37 32.5C37 33 37 33 35 33C33 33 33 33 33 32.5ZM56 32.5C56 32.06 55.94 32 55.5 32C55 32 55 32 55 30.5C55 29 55 29 55.5 29C55.94 29 56 28.94 56 28.5C56 28.06 56.06 28 56.5 28C56.94 28 57 27.94 57 27.5C57 27.06 57.06 27 57.5 27C58 27 58 27 58 26C58 25 58 25 58.5 25C59 25 59 25 59 23.5C59 22 59 22 59.5 22C60 22 60 22 60 18.5C60 15 60 15 59.5 15C59 15 59 15 59 14C59 13 59 13 58.5 13C58 13 58 13 58 12C58 11 58 11 57.5 11C57.06 11 57 10.94 57 10.5C57 10.06 56.94 10 56.5 10C56 10 56 10 56 9C56 8 56 8 55.5 8C55 8 55 8 55 7C55 6 55 6 55.5 6C55.94 6 56 5.94 56 5.5C56 5.06 56.06 5 56.5 5C56.94 5 57 4.94 57 4.5C57 4 57 4 58 4C59 4 59 4 59 4.5C59 5 59 5 60 5C61 5 61 5 61 6C61 7 61 7 61.5 7C61.94 7 62 7.06 62 7.5C62 7.94 62.06 8 62.5 8C63 8 63 8 63 9C63 10 63 10 63.5 10C64 10 64 10 64 11C64 12 64 12 64.5 12C65 12 65 12 65 13.5C65 15 65 15 65.5 15C66 15 66 15 66 18.5C66 22 66 22 65.5 22C65 22 65 22 65 24C65 26 65 26 64.5 26C64 26 64 26 64 27C64 28 64 28 63.5 28C63 28 63 28 63 29C63 30 63 30 62.5 30C62.06 30 62 30.06 62 30.5C62 30.94 61.94 31 61.5 31C61.06 31 61 31.06 61 31.5C61 31.94 60.94 32 60.5 32C60.06 32 60 32.06 60 32.5C60 33 60 33 58 33C56 33 56 33 56 32.5Z"
                fillRule="evenodd"
              />
            </svg>
          </button>

          {/* ATC instruction text (Title <-> Answer via RES) */}
          <div className="tap-prompt-text">
            {showAtc && atcMessage ? (
              <span className="reveal-answer atc-text">{atcMessage}</span>
            ) : reveal ? (
              <span className="reveal-answer">{revealText || "Answer"}</span>
            ) : (
              currentSlide?.title ||
              (currentSlide?.kind === "instruction" ? "Instruction" : "")
            )}
          </div>

          {/* Mic = record reply (same handler as old Mic button) */}
          <div className="mic-wrap">
            <button
              className={`tap-mic-btn ${
                isVoice ? (recording ? "is-recording" : "pulse-red") : ""
              }`}
              onClick={() => {
                if (currentResponse?.type !== "voice") return;
                setRecording((r) => !r);
              }}
              disabled={
                currentResponse?.type !== "voice" ||
                (micPhase === "analyzing" && !recording)
              }
              title={recording ? "Stop" : "Record"}
              aria-label={recording ? "Stop" : "Record your reply"}
            >
              {!recording && <FaMicrophone size={18} />}
              {recording && <FaStop size={18} />}
            </button>
            {recording && (
              <div className="mic-timer">
                {Math.floor(recSeconds / 60)}:
                {String(recSeconds % 60).padStart(2, "0")}
              </div>
            )}
          </div>
        </div>

        {/* ===== Control bar: ATC eye + pager (prev / help / next) + RES ===== */}
        <div className="tap-control-bar">
          {/* ATC = hear/show ATC (same handler as the tower button) */}
          <button
            className={`tap-eye-btn ${showAtc ? "is-on" : ""} ${
              atcEnabled && !showAtc && !isGateCheck ? "atc-blink" : ""
            }`}
            onClick={() => {
              if (!atcEnabled || isGateCheck) return;

              // ATC eye = SHOW/HIDE the ATC text only (no audio). Mirrors RES.
              if (showAtc) {
                setShowAtc(false);
                return;
              }

              const s = currentSlide;
              if (!s) return;

              if ((s as any).atc?.text) {
                setAtcMessage((s as any).atc.text);
                setShowAtc(true);
                return;
              }

              const script = (s as any).script as
                | Array<{ text: string; tts?: boolean }>
                | undefined;

              if (script?.length) {
                const pick = script.some((l) => l.tts)
                  ? script.filter((l) => l.tts).map((l) => l.text)
                  : script.map((l) => l.text);

                setAtcMessage(pick.join(" "));
                setShowAtc(true);
                return;
              }

              if (s.title) {
                setAtcMessage(s.title);
                setShowAtc(true);
              }
            }}
            disabled={!atcEnabled || isGateCheck}
            title={isGateCheck ? "Not available in a gate check" : "Show what ATC said"}
            aria-label="Show what ATC said"
          >
            <FaEye size={18} />
            <span className="tap-eye-label">ATC</span>
          </button>

          <div className="tap-pager">
            {/* Prev */}
            <button
              className="tap-pager-arrow"
              onClick={() => {
                prevTone.play(420);
                prev();
              }}
              disabled={idx <= 0}
              title="Previous"
              aria-label="Previous"
            >
              <IoArrowBackOutline size={20} />
            </button>

            {/* Help (old Check) */}
            <button
              className="tap-pager-help"
              onClick={doCheck}
              title={isGateCheck ? "Not available in a gate check" : "Help"}
              aria-label="Help"
              disabled={
                isGateCheck ||
                currentSlide?.kind === "instruction" ||
                !currentResponse
              }
            >
              ?
            </button>

            {/* Next — same disabled + blink condition */}
            <button
              className={`tap-pager-arrow ${
                nextEnabled && nextBlink[idx] ? "res-blink" : ""
              }`}
              onClick={() => {
                nextTone.play(720);
                // On the last instruction slide the arrow finishes the lesson
                // (nothing to answer, no next slide) instead of being a dead end.
                if (canFinishHere) {
                  handleFinalizeAndShow();
                  return;
                }
                next();
              }}
              disabled={!nextEnabled}
              title={canFinishHere ? "Finish" : "Next"}
              aria-label={canFinishHere ? "Finish" : "Next"}
            >
              <IoArrowForwardOutline size={20} />
            </button>
          </div>

          {/* RES = reveal expected answer (same handler) */}
          <button
            className={`tap-eye-btn ${reveal ? "is-on" : ""} ${
              // Not while it's ON: the blink's !important blue hid the green
              // "on" colour, so RES looked the same on and off.
              !reveal &&
              !graded &&
              !hasAnsweredCurrent &&
              !isGateCheck &&
              currentSlide?.kind === "question"
                ? "res-blink"
                : ""
            }`}
            onClick={() => {
              if (isGateCheck) return;
              setReveal((v) => !v);
            }}
            title={
              isGateCheck
                ? "Not available in a gate check"
                : "Reveal expected answer"
            }
            aria-label="Show the expected answer"
            disabled={
              isGateCheck ||
              currentSlide?.kind === "instruction" ||
              !currentResponse
            }
          >
            <FaEye size={18} />
            <span className="tap-eye-label">RES</span>
          </button>
        </div>

        <ProgressSingle
          value={answeredPct}
          animateTrigger={animKey}
          mode="idle"
          sweepMs={SWEEP_MS}
          onAfterAnimate={afterProgressAnim}
          //  showFeedback={showFeedbackLink && hasServerEval}
          onFeedback={() => (hasServerEval ? setShowFb(true) : undefined)}
        />
      </div>

      {/* bottom row — timer + finalize */}
      <div className="tap-footer">
        <div className="tap-footer-center">
          {showTimer ? (
            <div
              className={`tap-timer ${
                timeLeft !== null && timeLeft <= 15 ? "is-warning" : ""
              }`}
            >
              {timeLeft == null
                ? "—:—"
                : `${Math.floor((timeLeft || 0) / 60)}:${String(
                    (timeLeft || 0) % 60,
                  ).padStart(2, "0")}`}
            </div>
          ) : null}
        </div>
        {/* button section intentionally removed per your requirement */}
      </div>

      {/* check banner */}
      {checkMsg && (
        <div className={`tap-check-banner ${checkMsg === "ok" ? "ok" : "bad"}`}>
          {checkMsg === "ok" ? "✅ Correct" : "❌ Not quite"}
        </div>
      )}

      {/* Feedback modal */}
      {currentSlide && currentResponse && (
        <FeedbackModal
          show={showFb && hasServerEval}
          slide={currentSlide as Slide}
          response={currentResponse as Response}
          userAnswer={currentAnswer}
          onClose={() => setShowFb(false)}
          onApplyVoiceCorrection={async ({ transcript }) => {
            await submitAnswer(uuid, (currentResponse as Response).id, {
              transcript,
            });
            setShowFb(false);
          }}
          onMarkCorrect={async () => {
            if (!currentResponse) return;
            await WebService.postAPI({
              action: `/training/attempts/${uuid}/answers/${currentResponse.id}/override`,
              body: { is_correct: true },
            });
            await refetch();
            setShowFb(false);
          }}
          onHearExpected={() => {
            if (revealText) speakSequence([revealText]);
          }}
          onHearUser={() => {
            const raw = currentAnswer?.user_response?.url;
            if (!raw) return;
            const href = /^https?:\/\//i.test(raw)
              ? raw
              : baseImageUrl
                ? `${String(baseImageUrl).replace(/\/+$/, "")}/${String(
                    raw,
                  ).replace(/^\/+/, "")}`
                : raw;
            try {
              const a = new Audio(href);
              a.play().catch(() => {});
            } catch {}
          }}
        />
      )}

      {/* <LessonResultModal
        show={showResult}
        attemptUuid={uuid}
        onClose={() => setShowResult(false)}
        onRepeat={(attemptUuid, courseLessonUuid) => {
          // start a fresh run of the same lesson
          const lessonId = snapshot?.attempt?.course_lesson_id;
          if (lessonId) {
            setShowResult(false);
            if (isFromApp.current) {
              const url = new URL(window.location.href);
              url.searchParams.set("action", "repeat");
              window.location.href = url.toString(); // WebView will see this
            } else {
              navigate(
                `/training/lesson/detail/${courseLessonUuid}?autostart=true`,
              );
            }
          } else {
            setShowResult(false);
          }
        }}
        onCorrect={(courseLessonUuid) => {
          // simple action: close result and return to attempt view (you can refine later)
          setShowResult(false);
          if (isFromApp.current) {
            const url = new URL(window.location.href);
            url.searchParams.set("action", "correct");
            window.location.href = url.toString(); // WebView will see this
          } else {
            navigate(
              `/training/lesson/detail/${courseLessonUuid}?autostart=true`,
            );
          }
        }}
        onEndLesson={(courseLevelUuid) => {
          // go back to dashboard (or any landing page you use)
          setShowResult(false);
          if (isFromApp.current) {
            const url = new URL(window.location.href);
            url.searchParams.set("action", "end-lesson");
            window.location.href = url.toString(); // WebView will see this
          } else {
            navigate(`/training/lesson/${courseLevelUuid}`);
          }
        }}
      /> */}

      <LessonResultModal
        show={showResult}
        attemptUuid={uuid}
        onClose={() => setShowResult(false)}
        onRepeat={(attemptUuid, courseLessonUuid) => {
          setShowResult(false);
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "LESSON_ACTION", action: "repeat" }),
            );
          } else {
            navigate(
              `/training/lesson/detail/${courseLessonUuid}?autostart=true`,
            );
          }
        }}
        onCorrect={(courseLessonUuid) => {
          setShowResult(false);

          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "LESSON_ACTION", action: "correct" }),
            );
          } else {
            navigate(
              `/training/lesson/detail/${courseLessonUuid}?autostart=true`,
            );
          }
        }}
        onEndLesson={(courseLevelUuid) => {
          console.log("End Lesson Clicked");
          console.log(" courseLevelUuid:", courseLevelUuid);
          console.log(" Is ReactNativeWebView:", !!window.ReactNativeWebView);

          setShowResult(false);

          if (window.ReactNativeWebView) {
            console.log("Sending postMessage to React Native");

            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: "LESSON_ACTION",
                action: "end-lesson",
                courseLevelUuid: courseLevelUuid, // optional but useful
              }),
            );

            // Also navigate the WebView itself to the lesson page
            // so the user lands on /training/lesson/{uuid} instead of
            // just seeing the modal close.
            navigate(`/training/lesson/${courseLevelUuid}`);
          } else {
            console.log("🌐 Navigating to level page");

            navigate(`/training/lesson/${courseLevelUuid}`);
          }
        }}
      />

      {showHelp && helpData && (
        <Modal
          show={showHelp}
          size="xl"
          centered
          dialogClassName="help-dialog"
          contentClassName="help-modal"
          backdrop="static"
          keyboard={false}
        >
          <div className="help-header">
            <h2>Help</h2>
            <button className="help-done" onClick={() => setShowHelp(false)}>
              Done
            </button>
          </div>

          <div className="help-body">
            {/* SITUATION */}
            <section
              className="help-section"
              style={{
                width: "450px",
                marginLeft: "165px",
              }}
            >
              <div className="help-section-label">SITUATION</div>

              <div className="help-card">
                {helpData.atcInstruction && (
                  <>
                    <div className="help-card-title">ATC Instructions</div>
                    <div className="help-text border-b">
                      {helpData.atcInstruction}
                    </div>
                  </>
                )}

                {helpData.scriptMessages.map((m: any, i: number) => (
                  <div key={i} className="help-row">
                    <span>{m.label}</span>
                    <strong>{m.value}</strong>
                  </div>
                ))}
              </div>
            </section>

            {/* CONVERSATION */}
            <section className="help-section">
              <div className="help-section-label">CONVERSATION</div>

              <div className="help-card">
                <div className="help-card-title">ATC</div>
                <div className="">{helpData.title}</div>

                <div className="help-muted">Expected Respond</div>

                <div className="help-expected">{helpData.expectedResponse}</div>
              </div>
            </section>

            {/* PRACTICE */}
            <section className="help-section">
              <div className="help-section-label">PRACTICE</div>

              <div className="help-card">
                <div className="help-text">{helpData.expectedResponse}</div>

                <button
                  className="help-audio"
                  onClick={() => speakSequence([helpData.expectedResponse])}
                >
                  🔊 Hear Expected Response
                </button>
              </div>
            </section>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TrainingAttemptPlayer;
